import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import './styles/NoticeBoardView.scss';

import { Chip, Stack, Typography } from '@mui/material';
import { AttachFile, Delete, Edit, Groups, Link } from '@mui/icons-material';

import {
  Card,
  CenterProgressSpinner,
  DragAndDropContextProvider,
  DragItemTypes,
  EmptyListCTA,
  HStack,
  IconButton,
  SortableList
} from '@dw/components/core';
import { useConfirm } from '@dw/hooks';
import { useDeleteNoticeMutation, useGetNotices, useReorderNoticesMutation } from '@dw/hooks/api/NoticesApi';
import { INotice, NOTICE_STATUS, getNoticeStatus, resolveNoticeThemeStyle } from '@dw/models/notice';
import { APP_ICONS } from '@dw/constants/appIcons';
import { componentClassName } from '@dw/utils';

import { NoticeEditorModal } from './NoticeEditorModal';
import { NoticeThemeSwatch } from './NoticeThemeSwatch';

/** MUI Chip colours per schedule state. Expired is muted, not alarming — it is a normal end. */
const STATUS_COLOUR: Record<NOTICE_STATUS, 'success' | 'info' | 'default'> = {
  [NOTICE_STATUS.LIVE]: 'success',
  [NOTICE_STATUS.SCHEDULED]: 'info',
  [NOTICE_STATUS.EXPIRED]: 'default'
};

/**
 * The studio's noticeboard, as the people who write it see it.
 *
 * Two things this view does that the apps' board deliberately does not: it shows EVERY notice
 * whatever its schedule (the one scheduled for next week and the one that expired last term are
 * precisely what the person arranging a board needs to see), and it is reorderable — the order here
 * is the order families see.
 */
export const NoticeBoardView: FunctionComponent = () => {

  const { confirm } = useConfirm();

  const { data: notices, isLoading } = useGetNotices();
  const { mutate: reorderNotices } = useReorderNoticesMutation();
  const { mutate: deleteNotice } = useDeleteNoticeMutation();

  const [editorOpen, setEditorOpen] = useState(false);
  const [noticeBeingEdited, setNoticeBeingEdited] = useState<INotice>();

  /**
   * The order on screen, held locally so a drag lands instantly instead of waiting for the round
   * trip. The server is still the authority — it restates the whole board on save, and the query
   * invalidation below re-seeds this from that answer.
   */
  const [orderedNotices, setOrderedNotices] = useState<INotice[]>([]);

  useEffect(() => {
    setOrderedNotices([...(notices ?? [])].sort((a, b) => a.displayOrder - b.displayOrder));
  }, [notices]);

  const items = useMemo(
    () => orderedNotices.map(notice => ({ ...notice, id: notice.noticeId })),
    [orderedNotices]
  );

  const handleOrderChanged = (orderedIds: string[]) => {
    // Optimistic: reflect the drop immediately, then persist the COMPLETE order. Sending the whole
    // list rather than "moved X to N" is what makes a retry harmless and stops the board drifting.
    const byId = new Map(orderedNotices.map(x => [x.noticeId, x]));
    setOrderedNotices(orderedIds.map(id => byId.get(id)).filter((x): x is INotice => !!x));

    reorderNotices({ noticeIds: orderedIds });
  };

  const handleAdd = () => {
    setNoticeBeingEdited(undefined);
    setEditorOpen(true);
  };

  const handleEdit = (notice: INotice) => {
    setNoticeBeingEdited(notice);
    setEditorOpen(true);
  };

  const handleDelete = (notice: INotice) => {
    confirm({
      title: 'Remove this notice?',
      message: 'It comes off the board for everyone straight away. This cannot be undone.',
      positiveText: 'Remove notice',
      onPositive: () => deleteNotice(notice.noticeId)
    });
  };

  const cn = componentClassName('notice-board-view');

  const renderNotice = (notice: INotice & { id: string }) => {
    const status = getNoticeStatus(notice);

    return (
      <HStack className={cn('row')} columnGap={1.5} alignItems='center'>
        <NoticeThemeSwatch style={resolveNoticeThemeStyle(notice)} />

        <Stack rowGap={0.25} flex={1} minWidth={0}>
          {/* The studio's own heading when there is one. A notice without a title shows its body
              here instead — never a heading invented from the body's first line. */}
          <Typography noWrap fontWeight={notice.title ? 500 : 400}>
            {notice.title || notice.text}
          </Typography>

          {notice.title && (
            <Typography variant='caption' noWrap>{notice.text}</Typography>
          )}
        </Stack>

        <HStack columnGap={1} alignItems='center'>
          {notice.visibleToTeachers && (
            <Chip size='small' variant='outlined' icon={<Groups />} label='Teachers' />
          )}

          {notice.attachments.length > 0 && (
            <Chip size='small' variant='outlined' icon={<AttachFile />} label={notice.attachments.length} />
          )}

          {notice.links.length > 0 && (
            <Chip size='small' variant='outlined' icon={<Link />} label={notice.links.length} />
          )}

          <Chip size='small' color={STATUS_COLOUR[status]} label={status} />
        </HStack>
      </HStack>
    );
  };

  const renderRowActions = (notice: INotice & { id: string }) => (
    <>
      <IconButton icon={<Edit />} tooltip='Edit notice' onClick={() => handleEdit(notice)} />
      <IconButton icon={<Delete />} tooltip='Remove notice' color='error' onClick={() => handleDelete(notice)} />
    </>
  );

  const renderBody = () => {
    if (isLoading) {
      return <CenterProgressSpinner />;
    }

    if (!items.length) {
      return (
        <EmptyListCTA
          icon={APP_ICONS.NOTICE_BOARD}
          useCardWrapper
          body='Nothing is on the board yet. Notices appear at the top of the Home screen in the Dance World app — term dates, concert news, reminders.'
          ctaText='Write the first notice'
          ctaClicked={handleAdd}
        />
      );
    }

    return (
      <Card>
        {/* Exactly one provider above every SortableList on the page — nested providers shadow each
            other's drop-zone registry. */}
        <DragAndDropContextProvider>
          <SortableList
            items={items}
            dragType={DragItemTypes.NOTICE_BOARD}
            renderItem={renderNotice}
            rowActions={renderRowActions}
            onOrderChanged={handleOrderChanged}
          />
        </DragAndDropContextProvider>
      </Card>
    );
  };

  return (
    <Stack rowGap={2}>
      {items.length > 0 ? (
        <HStack justifyContent='space-between' alignItems='center'>
          <Typography variant='caption'>
            Drag to reorder — this is the order families see in the app.
          </Typography>
        </HStack>
      ) : null}

      {renderBody()}

      <NoticeEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        notice={noticeBeingEdited}
      />
    </Stack>
  );
};
